import type { AddressSuggestion, DeliveryZone, GeoPoint } from '@@partner:types:app'

import React, { useState, useCallback, useMemo } from 'react'
import { Button, Panel, Message } from 'rsuite'
import { isPointInPolygon } from 'geolib'
import { generatePath, Link } from 'react-router-dom'
import AddressAutosuggest from '@components/order/form/AddressAutoSuggest'
import { ROUTE_DELIVERY_ZONES_EDIT } from '@@partner:config/routes'
import { SLOT_DELIVERY_PROVIDERS, EXPRESS_DELIVERY_PROVIDERS } from '@@partner:config/deliveryZone'

import './AddressTestControl.scss'

interface AddressTestControlProps {
  zones: DeliveryZone[]
  brandSlug: string
  onAddressSelected?: (point: GeoPoint, address: string) => void
}

// Получить название провайдера
const getProviderLabel = (providerId: string): string => {
  const allProviders = [ ...SLOT_DELIVERY_PROVIDERS, ...EXPRESS_DELIVERY_PROVIDERS ]
  const provider = allProviders.find(p => p.value === providerId)
  return provider?.label ?? providerId
}

export default function AddressTestControl ({
  zones,
  brandSlug,
  onAddressSelected
}: AddressTestControlProps) {
  const [ address, setAddress ] = useState('')
  const [ selectedPoint, setSelectedPoint ] = useState<GeoPoint | null>(null)
  const [ matchingZones, setMatchingZones ] = useState<DeliveryZone[]>([])
  const [ isSearched, setIsSearched ] = useState(false)

  const handleAddressChange = useCallback((value: string) => {
    setAddress(value)
    setIsSearched(false)
  }, [])

  const handleSuggestionSelected = useCallback((
    _event: React.FormEvent<any>,
    data: any
  ) => {
    const suggestion = data.suggestion || data
    const lat = suggestion?.data?.geo_lat
    const lon = suggestion?.data?.geo_lon

    if (lat && lon) {
      const point: GeoPoint = {
        latitude: parseFloat(lat),
        longitude: parseFloat(lon)
      }
      setSelectedPoint(point)
      setAddress(suggestion.value || '')
      
      // Находим зоны, которые покрывают эту точку
      const foundZones = zones.filter(zone => {
        if (!zone.zoneGeoDeliveryZone || zone.zoneGeoDeliveryZone.length < 3) {
          return false
        }
        
        const polygonPoints = zone.zoneGeoDeliveryZone.map(p => ({
          lat: p.latitude ?? 0,
          lng: p.longitude ?? 0
        }))
        
        return isPointInPolygon(
          { lat: point.latitude ?? 0, lng: point.longitude ?? 0 },
          polygonPoints
        )
      })

      setMatchingZones(foundZones)
      setIsSearched(true)
      onAddressSelected?.(point, suggestion.value || '')
    }
  }, [ zones, onAddressSelected ])

  const handleReset = useCallback(() => {
    setAddress('')
    setSelectedPoint(null)
    setMatchingZones([])
    setIsSearched(false)
  }, [])

  return (
    <div className="AddressTestControl">
      <Panel bordered header="Тест адреса" collapsible defaultExpanded={false}>
        <div className="AddressTestControl__input">
          <AddressAutosuggest
            value={address}
            onChange={handleAddressChange}
            onSuggestionSelected={handleSuggestionSelected}
            onReset={handleReset}
            inputProps={{
              placeholder: 'Введите адрес для проверки...',
              className: 'AddressTestControl__field'
            }}
          />
        </div>

        {isSearched && (
          <div className="AddressTestControl__results">
            {matchingZones.length > 0 ? (
              <>
                <Message type="success" showIcon>
                  Найдено зон: {matchingZones.length}
                </Message>
                <div className="AddressTestControl__zones">
                  {matchingZones.map(zone => (
                    <div key={zone.id} className="AddressTestControl__zone">
                      <div className="AddressTestControl__zone-header">
                        <span className="AddressTestControl__zone-id">#{zone.id}</span>
                        <span className="AddressTestControl__zone-name">{zone.name || 'Без названия'}</span>
                        <Link
                          to={generatePath(ROUTE_DELIVERY_ZONES_EDIT, {
                            brandSlug,
                            zoneId: zone.id
                          })}
                          target="_blank"
                          className="AddressTestControl__zone-link"
                        >
                          Открыть
                        </Link>
                      </div>
                      {zone.shop && (
                        <div className="AddressTestControl__zone-shop">
                          Магазин: {zone.shop.name || `#${zone.shop_id}`}
                        </div>
                      )}
                      {zone.deliveryProviders && zone.deliveryProviders.length > 0 && (
                        <div className="AddressTestControl__zone-providers">
                          Провайдеры: {zone.deliveryProviders.map(getProviderLabel).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Message type="warning" showIcon>
                Адрес не входит ни в одну зону доставки
              </Message>
            )}
          </div>
        )}
      </Panel>
    </div>
  )
}
